import { PageHero } from "../components/PageHero";
import { Section, SectionHead, Card, Chip, ArrowLink } from "../components/ui";
import { Reveal } from "../components/Reveal";
import { Spine } from "../components/Spine";
import { Process } from "../sections/Process";
import { CTASection } from "../sections/CTASection";
import { PILLARS } from "../data/capabilities";
import { SPINE } from "../data/site";

/**
 * /what-we-do
 *
 * The page that exists to answer "so which of the three am I buying?" —
 * and to make clear that the answer can change mid-engagement without
 * changing supplier. That is the whole commercial argument for a company
 * this shape, so it gets a page rather than a paragraph.
 */
export function WhatWeDo() {
  return (
    <>
      <PageHero
        eyebrow="What we do"
        title={
          <>
            One delivery line.{" "}
            <span className="text-gradient">Three places to join it.</span>
          </>
        }
        lede="Think, design, build, deploy, operate. Consulting covers the front of that line, engineering covers the middle and end, and our products are the case where we walked all five stages ourselves and never handed the result over."
        primary={{ href: "/contact", label: "Start a project" }}
        secondary={{ href: "/work", label: "See the evidence" }}
      />

      <Section tight>
        <div className="container">
          <Reveal>
            <Card className="p-5 sm:p-8 lg:p-10">
              <Spine />
            </Card>
          </Reveal>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {SPINE.map((s, i) => (
              <Reveal key={s.key} delay={i * 0.05}>
                <div className="h-full rounded-xl border border-white/[0.08] bg-white/[0.02] p-5">
                  <div className="font-mono text-[11px] tabular-nums text-white/25">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-2 font-display text-base font-semibold tracking-tight">
                    {s.label}
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-white/55">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section>
        <div className="container">
          <SectionHead
            eyebrow="The three engagements"
            title={
              <>
                Enter anywhere.{" "}
                <span className="text-gradient">Move without changing supplier.</span>
              </>
            }
            lede="Most engagements start narrower than they end. A strategy question becomes a build; a build turns into something we operate. None of those transitions should require a new procurement round."
          />

          <div className="mt-10 space-y-4 sm:mt-14">
            {PILLARS.map((p, i) => (
              <Reveal key={p.key} delay={i * 0.06}>
                <Card
                  interactive
                  accent={p.key === "build" ? "violet" : "emerald"}
                  className="p-6 sm:p-8"
                >
                  <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:gap-10">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">
                        {p.short}
                      </div>
                      <h3 className="mt-1.5 font-display text-xl font-bold tracking-tight sm:text-2xl">
                        {p.title}
                      </h3>
                      <p className="mt-3 text-sm leading-relaxed text-white/65">
                        {p.lede}
                      </p>
                      <div className="mt-5">
                        <ArrowLink href={p.href}>{p.cta}</ArrowLink>
                      </div>
                    </div>

                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/35">
                        Covers
                      </div>
                      <div className="mt-2.5 flex flex-wrap gap-1.5">
                        {p.covers.map((c) => (
                          <span
                            key={c}
                            className="rounded border border-emerald-400/25 bg-emerald-400/10 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-emerald-200/90"
                          >
                            {SPINE.find((s) => s.key === c)?.label}
                          </span>
                        ))}
                      </div>

                      <div className="mt-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/35">
                        Inside it
                      </div>
                      <div className="mt-2.5 flex flex-wrap gap-1.5">
                        {p.capabilities.map((c) => (
                          <Chip key={c}>{c}</Chip>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Process />

      <CTASection />
    </>
  );
}
